/*
  Damano Kollektion: Filter und Sortierung ohne Neuladen.
  Holt die Sektion über die Section Rendering API (?section_id=…) und ersetzt Ergebnisse, Filter und Anzahl.
  «Marke» ist keine Shopify-Sortierung: mit marke=1 lädt das Skript alle Seiten und sortiert nach Vendor.
*/
if (!customElements.get('d-collection')) {
  class DamanoCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-filter-form]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-collection-strings]')?.textContent || '{}');
      } catch (e) {
        this.strings = {};
      }

      this.form?.addEventListener('change', () => this.apply(this.paramsFromForm()));
      this.form?.addEventListener('submit', (event) => event.preventDefault());

      this.addEventListener('click', (event) => {
        const filterLink = event.target.closest('[data-filter-link]');
        if (filterLink) {
          event.preventDefault();
          const params = new URL(filterLink.href, window.location.origin).searchParams;
          if (this.brandSort) params.set('marke', '1');
          this.apply(params);
          return;
        }
        const more = event.target.closest('[data-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
        }
      });

      this.querySelectorAll('[data-sort-select]').forEach((select) =>
        select.addEventListener('change', () => this.onSort(select.value))
      );

      window.addEventListener('popstate', () => this.apply(new URLSearchParams(window.location.search), false));

      const initial = new URLSearchParams(window.location.search);
      if (initial.get('marke') === '1') {
        this.brandSort = true;
        this.syncSortSelects('marke');
        this.sortByBrand();
      }
    }

    /* ---------- Parameter ---------- */

    paramsFromForm() {
      const params = new URLSearchParams();
      if (this.form) {
        for (const [key, value] of new FormData(this.form)) params.append(key, value);
      }
      if (this.brandSort) params.set('marke', '1');
      return params;
    }

    onSort(value) {
      this.brandSort = value === 'marke';
      this.syncSortSelects(value);
      const sortInput = this.querySelector('[data-sort-input]');
      if (sortInput) sortInput.value = this.brandSort ? this.dataset.defaultSort || 'manual' : value;
      const params = this.paramsFromForm();
      if (!this.form) {
        params.set('sort_by', this.brandSort ? this.dataset.defaultSort || 'manual' : value);
        if (this.brandSort) params.set('marke', '1');
      }
      this.apply(params);
    }

    syncSortSelects(value) {
      this.querySelectorAll('[data-sort-select]').forEach((select) => (select.value = value));
    }

    /* ---------- Laden und Ersetzen ---------- */

    async fetchSection(params) {
      const query = new URLSearchParams(params);
      query.delete('marke');
      query.set('section_id', this.sectionId);
      const response = await fetch(`${window.location.pathname}?${query.toString()}`);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async apply(params, push = true) {
      this.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(params);
        this.render(doc);
        if (push) {
          const query = params.toString();
          window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        }
        this.brandSort = params.get('marke') === '1';
        this.syncSortSelects(this.brandSort ? 'marke' : params.get('sort_by') || this.dataset.defaultSort || 'manual');
        if (this.brandSort) await this.sortByBrand();
      } catch (error) {
        console.error(error);
        window.location.search = params.toString();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(doc) {
      const swap = (selector) => {
        const source = doc.querySelector(selector);
        const target = this.querySelector(selector);
        if (source && target) target.innerHTML = source.innerHTML;
      };

      // Offene/geschlossene Filtergruppen beibehalten
      const openState = {};
      this.querySelectorAll('[data-filter-groups] details[id]').forEach((d) => (openState[d.id] = d.open));
      swap('[data-filter-groups]');
      this.querySelectorAll('[data-filter-groups] details[id]').forEach((d) => {
        if (d.id in openState) d.open = openState[d.id];
      });

      swap('[data-results]');
      swap('[data-filter-label]');
      swap('[data-show-label]');
      const count = doc.querySelector('[data-count]');
      if (count) this.querySelectorAll('[data-count]').forEach((el) => (el.innerHTML = count.innerHTML));
      const clear = doc.querySelector('.d-filters__clear');
      const clearTarget = this.querySelector('.d-filters__clear');
      if (clear && clearTarget) clearTarget.href = clear.getAttribute('href');
    }

    async loadMore(link) {
      link.classList.add('is-loading');
      const url = new URL(link.href, window.location.origin);
      const doc = await this.fetchSection(url.searchParams);
      this.appendPage(doc);
    }

    appendPage(doc) {
      const grid = this.querySelector('[data-grid]');
      const newGrid = doc.querySelector('[data-grid]');
      if (grid && newGrid) grid.append(...newGrid.children);
      const more = this.querySelector('.d-coll__more');
      const newMore = doc.querySelector('.d-coll__more');
      if (more) newMore ? more.replaceWith(newMore) : more.remove();
    }

    /* ---------- Sortierung nach Marke ---------- */

    async sortByBrand() {
      // Alle Seiten laden, damit die Sortierung über die ganze Auswahl gilt
      let more = this.querySelector('[data-load-more]');
      let guard = 0;
      while (more && guard < 20) {
        guard += 1;
        const url = new URL(more.href, window.location.origin);
        const doc = await this.fetchSection(url.searchParams);
        this.appendPage(doc);
        more = this.querySelector('[data-load-more]');
      }
      const grid = this.querySelector('[data-grid]');
      if (!grid) return;
      const items = [...grid.children];
      items.sort(
        (a, b) =>
          (a.dataset.vendor || '').localeCompare(b.dataset.vendor || '', 'de', { sensitivity: 'base' }) ||
          Number(a.dataset.index) - Number(b.dataset.index)
      );
      grid.append(...items);
    }
  }

  customElements.define('d-collection', DamanoCollection);
}
